import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, FileText, ImageIcon, RefreshCw, Mail } from 'lucide-react';
import { syncReviews } from '../services/reviewService';
import AdminNotificationBell from '../components/admin/AdminNotificationBell';

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    navigate('/admin/login');
  };

  const handleSyncReviews = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setSyncError(null);
    try {
      const result = await syncReviews();
      setSyncMessage(result.message || `Synced ${result.upserted} reviews`);
    } catch (err: any) {
      setSyncError(err?.message || 'Failed to sync reviews');
    } finally {
      setSyncing(false);
    }
  };

  const tiles = [
    {
      title: 'Tour Management',
      description: 'Create, edit and manage tour packages',
      path: '/admin/tours',
      icon: Package,
      gradientClass: 'gradient-primary-br',
    },
    {
      title: 'View Registrations',
      description: 'Access customer registration data',
      path: '/admin/registrations',
      icon: Users,
      gradientClass: 'gradient-emerald',
    },
    {
      title: 'Custom tour interests',
      description: 'View visitor interest for custom destinations',
      path: '/admin/custom-interests',
      icon: Mail,
      gradientClass: 'gradient-luxury-br',
    },
    {
      title: 'Custom Tours',
      description: 'Manage custom tour destinations and months',
      path: '/admin/custom',
      icon: FileText,
      gradientClass: 'gradient-emerald-blue',
    },
    {
      title: 'Gallery Management',
      description: 'Upload and manage gallery images',
      path: '/admin/gallery',
      icon: ImageIcon,
      gradientClass: 'gradient-luxury-br',
    },
  ];

  return (
    <div className="min-h-screen gradient-admin-surface">
      {/* Header */}
      <div className="bg-solid-primary text-white shadow-lg">
        <div className="container mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold font-[var(--font-primary)]">Admin Dashboard</h1>
              <p className="text-sm mt-1 opacity-90">Manage your travel business</p>
            </div>
            <div className="flex items-center gap-4">
              <AdminNotificationBell variant="onPrimary" />
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-white text-[var(--primary-deep-blue)] rounded-full font-semibold text-sm transition-all duration-300 hover:bg-opacity-90 hover:scale-105 hover:shadow-lg"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Message */}
          <div className="text-center mb-12 animate-fade-in-up">
            <h2 className="heading-tertiary mb-3 text-[var(--neutral-charcoal)]">
              Welcome Back
            </h2>
            <p className="body-large text-[var(--secondary-slate)]">
              Select an option below to get started
            </p>
          </div>

          {/* Tile Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {tiles.map((tile, index) => {
              const IconComponent = tile.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(tile.path)}
                  className="group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 p-8 text-left border border-[var(--neutral-medium-gray)] hover:border-transparent"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Gradient Background on Hover */}
                  <div
                    className={`absolute inset-0 ${tile.gradientClass} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-neutral-br group-hover:from-white/20 group-hover:to-white/10 transition-all duration-500">
                      <IconComponent 
                        className="w-8 h-8 text-[var(--primary-deep-blue)] group-hover:text-white transition-colors duration-500" 
                      />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-3 text-[var(--neutral-charcoal)] group-hover:text-white transition-colors duration-500 font-[var(--font-primary)]">
                      {tile.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[var(--secondary-slate)] group-hover:text-white/90 transition-colors duration-500 font-[var(--font-secondary)]">
                      {tile.description}
                    </p>

                    {/* Arrow Icon */}
                    <div className="mt-6 flex items-center text-[var(--primary-deep-blue)] group-hover:text-white transition-colors duration-500">
                      <span className="text-sm font-semibold mr-2">Get Started</span>
                      <svg
                        className="w-5 h-5 transform group-hover:translate-x-2 transition-transform duration-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Decorative Corner Element */}
                  <div className="absolute top-0 right-0 w-24 h-24 gradient-accent-subtle rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </button>
              );
            })}
          </div>

          {/* Google Reviews Sync */}
          <div className="mt-12 bg-white rounded-2xl shadow-lg border border-[var(--neutral-medium-gray)] p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-[var(--neutral-charcoal)] font-[var(--font-primary)]">
                  Google Reviews
                </h3>
                <p className="body-regular text-[var(--secondary-slate)] mt-1">
                  Pull the latest reviews (with photos and owner replies) from Google Maps into the site.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSyncReviews}
                disabled={syncing}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white gradient-primary shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing…' : 'Sync Google Reviews'}
              </button>
            </div>
            {syncMessage && (
              <p className="mt-4 body-small text-[var(--success)]">{syncMessage}</p>
            )}
            {syncError && (
              <p className="mt-4 body-small text-[var(--error)]">{syncError}</p>
            )}
          </div>

          {/* Quick Stats or Additional Info (Optional - can be expanded later) */}
          <div className="mt-16 text-center">
            <p className="body-small text-[var(--secondary-slate)]">
              Need help? Contact technical support
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
